import type { ReactNode } from 'react';
import clsx from 'clsx';
import { IconChevronLeft } from './Icons';

/* ---------- class presets ---------- */

export const btnBase =
  'inline-flex items-center justify-center gap-2 rounded-[16px] font-bold transition-colors disabled:opacity-50 disabled:pointer-events-none';
export const btnPrimary = clsx(btnBase, 'h-14 px-6 bg-primary text-white active:bg-primary-dark');
export const btnDark = clsx(btnBase, 'h-14 px-6 bg-ink text-white active:opacity-90');
export const btnSoft = clsx(btnBase, 'h-12 px-5 bg-soft text-ink active:bg-line');
export const btnOutline = clsx(btnBase, 'h-12 px-5 border border-line bg-white text-ink active:bg-soft');
export const btnSm = '!h-10 !px-4 !text-sm !rounded-full';

export const input =
  'h-12 w-full rounded-[14px] bg-soft px-4 text-base text-ink placeholder:text-faint border border-transparent ' +
  'focus:bg-white focus:border-primary transition-colors';
export const inputError = '!border-bad-dot !bg-bad-soft';
export const textarea = clsx(input, 'h-auto min-h-[104px] py-3 resize-none');
export const label = 'block text-[13px] font-bold text-ink mb-1.5';
export const hint = 'text-xs text-muted mt-1.5';

export const chip =
  'inline-flex items-center gap-1 h-9 px-3.5 rounded-full text-[13px] whitespace-nowrap transition-colors';
export const chipOn = 'bg-ink text-white font-bold';
export const chipOff = 'bg-soft text-ink';

/* ---------- layout ---------- */

export function Screen({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx('min-h-screen bg-white text-ink', className)}>{children}</div>;
}

/** Compact top bar: back button, title, optional right slot. */
export function TopBar({ title, subtitle, onBack, backTo, right }: {
  title?: ReactNode;
  subtitle?: ReactNode;
  onBack?: () => void;
  backTo?: string;
  right?: ReactNode;
}) {
  const back = onBack || backTo ? (
    <BackButton onClick={onBack} to={backTo} />
  ) : null;
  return (
    <div className="safe-top flex items-center gap-3 px-5 pt-3 pb-3">
      {back}
      <div className="flex-1 min-w-0 flex flex-col leading-tight">
        {title && <span className="text-[15px] font-bold truncate">{title}</span>}
        {subtitle && <span className="text-xs text-muted truncate tabular">{subtitle}</span>}
      </div>
      {right}
    </div>
  );
}

export function BackButton({ onClick, to }: { onClick?: () => void; to?: string }) {
  const cls = 'w-10 h-10 shrink-0 rounded-full bg-soft text-ink inline-flex items-center justify-center active:bg-line';
  if (to) {
    return <a href={to} aria-label="返回" className={cls}><IconChevronLeft size={18} /></a>;
  }
  return (
    <button type="button" onClick={onClick} aria-label="返回" className={cls}>
      <IconChevronLeft size={18} />
    </button>
  );
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between">
      <h2 className="text-[15px] font-bold text-muted">{children}</h2>
      {action}
    </div>
  );
}

/* ---------- pieces ---------- */

/** Month + day tile used in event rows. */
export function DateTile({ date, color, muted }: { date?: string | null; color?: string; muted?: boolean }) {
  const d = date ? new Date(date) : null;
  const valid = d && !isNaN(d.getTime());
  return (
    <div
      className={clsx(
        'tabular w-14 shrink-0 flex flex-col items-center leading-none py-2 rounded-tile',
        muted ? 'bg-soft text-muted' : 'bg-white',
      )}
      style={!muted && color ? { color } : undefined}
    >
      <span className="text-[11px] font-bold">{valid ? `${d!.getMonth() + 1}月` : '日期'}</span>
      <span className="text-2xl font-black mt-1">{valid ? d!.getDate() : '－'}</span>
    </div>
  );
}

const statusMap: Record<string, { label: string; cls: string; dot: string }> = {
  pending:  { label: '待匯款', cls: 'bg-warn-soft text-warn', dot: 'bg-warn-dot' },
  verified: { label: '已確認', cls: 'bg-ok-soft text-ok',     dot: 'bg-ok-dot' },
  rejected: { label: '已取消', cls: 'bg-bad-soft text-bad',   dot: 'bg-bad-dot' },
};

export function statusLabel(status?: string) {
  return (status && statusMap[status]?.label) || status || '';
}

export function StatusPill({ status, className }: { status?: string; className?: string }) {
  const s = (status && statusMap[status]) || { label: status || '－', cls: 'bg-soft text-muted', dot: 'bg-faint' };
  return (
    <span className={clsx('inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-xs font-bold whitespace-nowrap', s.cls, className)}>
      <span aria-hidden="true" className={clsx('w-1.5 h-1.5 rounded-full', s.dot)} />
      {s.label}
    </span>
  );
}

export function Notice({ tone = 'info', icon, children }: { tone?: 'info' | 'warn' | 'bad' | 'ok'; icon?: ReactNode; children: ReactNode }) {
  const cls = {
    info: 'bg-soft text-[#4B5563]',
    warn: 'bg-warn-soft text-warn',
    bad:  'bg-bad-soft text-bad',
    ok:   'bg-ok-soft text-ok',
  }[tone];
  return (
    <div role={tone === 'bad' ? 'alert' : 'status'} className={clsx('flex gap-2.5 px-4 py-3.5 rounded-[16px] text-[13px] leading-relaxed', cls)}>
      {icon && <span className="shrink-0 mt-0.5">{icon}</span>}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

export function EmptyState({ icon, title, hint: text, action }: { icon?: ReactNode; title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center text-center gap-2 px-6 py-14">
      {icon && <span className="text-faint mb-1">{icon}</span>}
      <div className="text-[15px] font-bold">{title}</div>
      {text && <div className="text-[13px] text-muted max-w-xs">{text}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function formatNtd(n?: number | null) {
  return `NT$ ${(n || 0).toLocaleString('zh-TW')}`;
}

export function formatDate(dateStr?: string | null, opts?: Intl.DateTimeFormatOptions) {
  if (!dateStr) return '日期待定';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('zh-TW', opts || { month: 'numeric', day: 'numeric', weekday: 'short' });
}

export function formatDateTime(dateStr?: string | null) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleString('zh-TW', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Days until a deadline; negative when past. */
export function daysLeft(dateStr?: string | null): number | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86400000);
}
